import React from "react";
import { Pressable, Text, View } from "react-native";
import { ChevronRight, type LucideIcon } from "lucide-react-native";
import { EMBER } from "../../../onboarding/theme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * Shared brown palette + the single CTA primitive used across every button on
 * the pharmacy access screen (Prescription section, concierge sheet, Quotes
 * tab, etc.). Kept in one file so no surface can drift into a bespoke button.
 */

export const BROWN = "#6B5548";
export const BROWN_WASH_BG = "rgba(107, 85, 72, 0.12)";
export const BROWN_WASH_BG_STRONG = "rgba(107, 85, 72, 0.18)";
export const BROWN_LINE = "rgba(107, 85, 72, 0.18)";
export const WHITE_WASH = "rgba(255, 255, 255, 0.32)";

export type ConciergePrescriptionFile = {
  uri: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
};

export type ExtractedMedication = {
  name: string;
  dosage?: string;
};

/**
 * UI projection of the backend `PharmacyQuoteRequest.status`:
 *   - `idle`: no request, a `draft` (Rx uploaded, not sent) or a `cancelled`
 *     one; the Prescription tab "Get quotes" CTA is available
 *   - `waiting`: `submitted` — Louise ops are filling each pharmacy's quote
 *     form for the patient; per-pharmacy progress comes from `submissions`
 *   - `done`: every pharmacy has been handled. Pharmacies reply directly to
 *     the patient's inbox — we never learn who responded, so there's no
 *     `received` state. Once the patient uploads a received quote, they can
 *     compare + pick a pharmacy for the prescriber-side handoff (which the
 *     patient owns, not Louise).
 */
export type QuoteRequestStatus = "idle" | "waiting" | "done";

export function formatPriceCents(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

export function formatShortDate(iso: string): string {
  try {
    const date = new Date(iso);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export function formatFileSize(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Compact pill CTA for row-trailing / adjacent-to-content spots (e.g. the
 * per-pharmacy Upload button, the Copy-message button in the Ready-to-fill
 * card). For primary modal/section CTAs prefer `DialogPrimaryButton` /
 * `SoftSecondaryButton` from moon-ui; for empty-state upload tiles use
 * `ScanTileButton` below.
 */
export function PharmacyActionButton({
  icon: Icon,
  label,
  action,
  onPress,
  secondary = false,
  grow = false,
}: {
  icon: LucideIcon;
  label: string;
  /** i18n key of `label`, kept stable for analytics across locales. */
  action: string;
  onPress: () => void;
  secondary?: boolean;
  grow?: boolean;
}) {
  const capturePress = usePressCapture("PharmacyActionButton");
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={() => {
        capturePress(pressLabel(label), action);
        onPress();
      }}
      style={{
        flexGrow: grow ? 1 : 0,
        minHeight: 38,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        paddingHorizontal: 13,
        borderRadius: 999,
        borderWidth: secondary ? 1 : 0,
        borderColor: BROWN_LINE,
        backgroundColor: secondary ? WHITE_WASH : BROWN_WASH_BG,
      }}
    >
      <Icon size={14} color={BROWN} strokeWidth={1.8} />
      <Text
        className="font-dm-sans"
        style={{ color: BROWN, fontSize: 12, fontWeight: "800" }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

/**
 * Full-width tile CTA modeled on AddMedicationModal's "Scan prescription"
 * entry: a bordered card with a leading warm-tone icon, primary title,
 * supporting subtitle, and trailing chevron. Use for the top-level "invite
 * to start" actions — Upload prescription (empty state) and Upload a quote
 * from another pharmacy.
 */
export function ScanTileButton({
  icon: Icon,
  title,
  subtitle,
  action,
  onPress,
  disabled = false,
}: {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  action: string;
  onPress: () => void;
  disabled?: boolean;
}) {
  const capturePress = usePressCapture("ScanTileButton");
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      disabled={disabled}
      onPress={() => {
        capturePress(pressLabel(title), action);
        onPress();
      }}
      style={{
        flexDirection: "row",
        alignItems: "center",
        padding: 16,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: EMBER.colors.border,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <View
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          justifyContent: "center",
          alignItems: "center",
          marginRight: 14,
        }}
      >
        <Icon size={20} color={EMBER.colors.warm} />
      </View>
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            fontWeight: "600",
            color: EMBER.colors.text,
          }}
        >
          {title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textLight,
            marginTop: 2,
          }}
        >
          {subtitle}
        </Text>
      </View>
      <ChevronRight size={18} color={EMBER.colors.textFaint} />
    </Pressable>
  );
}
